import { Check, CircleAlert, Loader2 } from 'lucide-react';
import type { MeetlyProcessingStage } from '../api/meetly';

/**
 * The pipeline, drawn as a checklist instead of a spinner.
 *
 * The wait after a meeting has not only got shorter — it has become legible. A
 * spinner for four minutes and a spinner for forty seconds look identical, so the
 * old UI threw away the improvement it was showing. This says which step is running
 * and, while chunks are transcribing, how far through it is.
 */

export interface ProcessingProgress {
  transcribed?: number;
  total?: number;
  failed?: number;
}

interface Props {
  stage: MeetlyProcessingStage;
  progress?: ProcessingProgress | null;
  error?: string | null;
  /** Lets a FAILED meeting show the transcript step as done rather than failed. */
  hasTranscript?: boolean;
  className?: string;
}

type StepState = 'done' | 'active' | 'pending' | 'failed';

interface Step {
  key: MeetlyProcessingStage;
  label: string;
  activeLabel: string;
}

const STEPS: Step[] = [
  { key: 'PROCESSING', label: 'Meeting completed', activeLabel: 'Finishing the recording' },
  { key: 'TRANSCRIBING', label: 'Audio processed', activeLabel: 'Processing audio' },
  { key: 'TRANSCRIPT_READY', label: 'Transcript ready', activeLabel: 'Building the transcript' },
  { key: 'SUMMARIZING', label: 'Summary ready', activeLabel: 'Generating the summary' },
];

// Where each stage sits on the checklist. COMPLETED is past the last step, which is
// what marks every row done.
const STAGE_POSITION: Record<MeetlyProcessingStage, number> = {
  RECORDING: -1,
  PROCESSING: 0,
  TRANSCRIBING: 1,
  TRANSCRIPT_READY: 2,
  SUMMARIZING: 3,
  COMPLETED: 4,
  FAILED: -1,
};

const stepState = (
  index: number,
  stage: MeetlyProcessingStage,
  failedAt: number,
): StepState => {
  if (stage === 'FAILED') {
    // Only the step that failed is marked failed. Marking all four red said "the
    // transcript failed too", which is wrong for the common case — a summary that
    // failed on a meeting whose transcript is sitting right there, readable.
    if (index < failedAt) return 'done';
    if (index === failedAt) return 'failed';
    return 'pending';
  }
  const position = STAGE_POSITION[stage] ?? 0;
  if (index < position) return 'done';
  if (index === position) return 'active';
  return 'pending';
};

/**
 * How far the pipeline got before it failed, inferred from what exists.
 *
 * The meeting payload carries no "failed at" marker, but it does say whether a
 * transcript was produced, and that is the only distinction that changes what the
 * user should do next.
 */
const failurePosition = (progress?: ProcessingProgress | null, hasTranscript?: boolean) => {
  if (hasTranscript) return 3;              // transcript exists → the summary failed
  if ((progress?.transcribed ?? 0) > 0) return 2;  // some audio transcribed
  return 1;                                  // never got past the audio
};

const StepIcon = ({ state }: { state: StepState }) => {
  if (state === 'failed') {
    return <CircleAlert className="h-4 w-4 text-red-600" aria-hidden="true" />;
  }
  if (state === 'done') {
    return <Check className="h-4 w-4 text-emerald-600" aria-hidden="true" />;
  }
  if (state === 'active') {
    return <Loader2 className="h-4 w-4 animate-spin text-[var(--gt-primary)]" aria-hidden="true" />;
  }
  return (
    <span
      className="block h-2 w-2 rounded-full bg-[var(--gt-border-strong)]"
      aria-hidden="true"
    />
  );
};

export const MeetlyProcessingTimeline: React.FC<Props> = ({
  stage,
  progress,
  error,
  hasTranscript,
  className,
}) => {
  const transcribed = progress?.transcribed ?? 0;
  const total = progress?.total ?? 0;
  const failed = progress?.failed ?? 0;
  const showCounts = stage === 'TRANSCRIBING' && total > 0;
  const percent = total > 0 ? Math.min(100, Math.round((transcribed / total) * 100)) : 0;
  const failedAt = failurePosition(progress, hasTranscript);

  return (
    <div className={className} data-testid="meetly-processing-timeline">
      <ol className="space-y-3" aria-live="polite">
        {STEPS.map((step, index) => {
          const state = stepState(index, stage, failedAt);
          const isActive = state === 'active';
          return (
            <li key={step.key} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                <StepIcon state={state} />
              </span>
              <span className="min-w-0">
                <span
                  className={
                    isActive
                      ? 'text-sm font-medium text-[var(--gt-text-primary)]'
                      : state === 'done'
                        ? 'text-sm text-[var(--gt-text-primary)]'
                        : 'text-sm text-[var(--gt-text-tertiary)]'
                  }
                >
                  {isActive ? step.activeLabel : step.label}
                </span>
                {isActive && showCounts && (
                  <span className="mt-1 block text-xs text-[var(--gt-text-tertiary)]">
                    {transcribed} of {total} parts transcribed
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>

      {showCounts && (
        <div
          className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-[var(--gt-surface-hover)]"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-label="Transcription progress"
        >
          <div
            className="h-full rounded-full bg-[var(--gt-primary)] transition-[width] duration-500 ease-out"
            style={{ width: `${percent}%` }}
          />
        </div>
      )}

      {failed > 0 && stage !== 'FAILED' && (
        <p className="mt-3 text-xs text-[var(--gt-text-tertiary)]">
          {failed === 1 ? 'One part' : `${failed} parts`} of the audio could not be
          processed. The rest of the meeting is unaffected.
        </p>
      )}

      {stage === 'FAILED' && (
        <p className="mt-3 text-sm text-red-600">
          {error || 'This meeting could not be processed. You can try again.'}
        </p>
      )}
    </div>
  );
};

export default MeetlyProcessingTimeline;
